import { ICompanyInviteCodeDetail } from '@/types/response/company';
import type { ICompanyInviteCodeRequest } from '@/types/request/company';
import type { IInviteCodeRow, IValidatedErrorRow, IValidatedField } from './types';
import moment from 'moment';

function field<T>(value: T, isError: boolean, color: string | null): IValidatedField<T> {
  return { value, isError, color };
}

const ERROR_CELL_BG = '#ffcccc';
/** Chỉ chấp nhận đúng format YYYY-MM-DD（有効開始日・有効終了日）. */
function isValidDate(v: string | null): boolean {
  if (v === null || String(v).trim() === '') return true;
  const trimmed = String(v).trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return false;
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return false;
  const [y, m, day] = trimmed.split('-').map(Number);
  return (
    d.getFullYear() === y &&
    d.getMonth() === m - 1 &&
    d.getDate() === day
  );
}

/** Parse YYYY-MM-DD thành Date (00:00:00 local). Trả về null nếu invalid. */
function parseYYYYMMDD(v: string | null): moment.Moment | null {
  if (v === null) return null;
  const m = moment(String(v).trim(), 'YYYY-MM-DD', true);
  if (!m.isValid()) return null;
  return m.startOf('day');
}

function toYYYYMMDD(v: string | null | undefined): string | null {
  if (!v) return null;
  const m = moment(String(v).trim());
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
}

/** Ngày hôm nay 00:00:00 (local). */
function getTodayStart(): moment.Moment {
  return moment().startOf('day');
}

const WARNING_CELL_BG = '#fff3b0';
const MAX_INVITE_CODE_LENGTH = 30;

export function validateInviteCodeRows(
  rows: IInviteCodeRow[],
  conflictingCodes: string[] = [],
): { errors: IValidatedErrorRow[]; success: IInviteCodeRow[] } {
  const errors: IValidatedErrorRow[] = [];
  const success: IInviteCodeRow[] = [];
  const seenIds = new Set<number>();
  const seenCodes = new Set<string>();
  const conflictSet = new Set(conflictingCodes.map((c) => String(c).trim()));

  for (const row of rows) {
    const idInvalid =
      row.id === null ||
      row.id === undefined ||
      typeof row.id !== 'number' ||
      Number.isNaN(row.id) ||
      row.id <= 0;
    const codeNormalized = String(row.code ?? '').trim();
    const codeEmpty = codeNormalized === '';
    const codeSpaceError = /\s/.test(codeNormalized);
    // half-width only (ASCII)
    const codeHalfWidthError =
      codeNormalized !== '' && !/^[\x20-\x7E]+$/.test(codeNormalized);
    const codeKey = codeNormalized;
    const codeMaxLenError = codeNormalized.length > MAX_INVITE_CODE_LENGTH;
    const codeDuplicate = codeKey !== '' && seenCodes.has(codeKey);
    const codeDbConflict = codeKey !== '' && conflictSet.has(codeKey);
    const codeError =
      codeEmpty ||
      codeSpaceError ||
      codeHalfWidthError ||
      codeMaxLenError ||
      codeDuplicate ||
      codeDbConflict;
    const todayStart = getTodayStart();
    const validFromDate = parseYYYYMMDD(row.valid_from);
    const validToDate = parseYYYYMMDD(row.valid_to);
    const validFromError =
      row.valid_from === null ||
      !isValidDate(row.valid_from) ||
      (validFromDate !== null && validFromDate.isBefore(todayStart));
    const validToError =
      row.valid_to !== null &&
      !isValidDate(row.valid_to) ||
      (validFromDate !== null &&
        validToDate !== null &&
        validToDate.isBefore(validFromDate));
    const cogEvoError =
      row.cog_evo === null || (row.cog_evo !== 0 && row.cog_evo !== 1);
    const nounowError =
      row.nounow === null || (row.nounow !== 0 && row.nounow !== 1);
    const limitError =
      row.limit === null ||
      typeof row.limit !== 'number' ||
      Number.isNaN(row.limit) ||
      row.limit <= 0;

    const idValue =
      typeof row.id === 'number' && !Number.isNaN(row.id) ? row.id : null;
    const hasIdValue =
      row.id !== null &&
      row.id !== undefined &&
      typeof row.id === 'number' &&
      !Number.isNaN(row.id);
    const idDuplicate = idValue !== null && seenIds.has(idValue);

    const idError = idInvalid || idDuplicate;

    const hasOtherError =
      codeError ||
      validFromError ||
      validToError ||
      cogEvoError ||
      nounowError ||
      limitError;

    if (idError || hasOtherError) {
      const codeColor = codeDbConflict ? WARNING_CELL_BG : (codeError ? ERROR_CELL_BG : null);
      errors.push({
        id: field(
          row.id_raw ?? row.id,
          idError,
          idError ? ERROR_CELL_BG : null,
        ),
        code: field(row.code, codeError, codeColor),
        valid_from: field(
          row.valid_from,
          validFromError,
          validFromError ? ERROR_CELL_BG : null,
        ),
        valid_to: field(
          row.valid_to,
          validToError,
          validToError ? ERROR_CELL_BG : null,
        ),
        cog_evo: field(
          row.cog_evo_raw ?? row.cog_evo,
          cogEvoError,
          cogEvoError ? ERROR_CELL_BG : null,
        ),
        nounow: field(
          row.nounow_raw ?? row.nounow,
          nounowError,
          nounowError ? ERROR_CELL_BG : null,
        ),
        limit: field(
          row.limit_raw ?? row.limit,
          limitError,
          limitError ? ERROR_CELL_BG : null,
        ),
      });
    } else {
      success.push(row);
    }
    // Luôn ghi nhận id/code đã gặp (dù dòng vào success hay error) để phát hiện trùng ở các dòng sau
    if (idValue !== null) seenIds.add(idValue);
    if (codeKey !== '') seenCodes.add(codeKey);
  }

  errors.sort((a, b) => {
    const aNum =
      typeof a.id.value === 'number' ? a.id.value : Number.MAX_SAFE_INTEGER;
    const bNum =
      typeof b.id.value === 'number' ? b.id.value : Number.MAX_SAFE_INTEGER;
    return aNum - bNum;
  });
  return { errors, success };
}

export function validateInviteCodeUpdateRows(
  rows: IInviteCodeRow[],
  conflictingCodes: string[] = [],
  inviteCodes: ICompanyInviteCodeDetail[] = [],
): {
  errors: IValidatedErrorRow[];
  success: IInviteCodeRow[];
  updateData: ICompanyInviteCodeRequest[];
} {
  const updateData: ICompanyInviteCodeRequest[] = [];
  const errors: IValidatedErrorRow[] = [];
  const success: IInviteCodeRow[] = [];
  const seenIds = new Set<number>();
  const seenCodes = new Set<string>();
  const conflictSet = new Set(conflictingCodes.map((c) => String(c).trim()));

  for (const row of rows) {
    const idInvalid =
      row.id === null ||
      row.id === undefined ||
      typeof row.id !== 'number' ||
      Number.isNaN(row.id) ||
      row.id <= 0;
    const codeNormalized = String(row.code ?? '').trim();
    const codeEmpty = codeNormalized === '';
    const codeSpaceError = /\s/.test(codeNormalized);
    // half-width only (ASCII)
    const codeHalfWidthError =
      codeNormalized !== '' && !/^[\x20-\x7E]+$/.test(codeNormalized);
    const codeKey = codeNormalized;
    const codeMaxLenError = codeNormalized.length > MAX_INVITE_CODE_LENGTH;
    const codeDuplicate = codeKey !== '' && seenCodes.has(codeKey);
    const codeDbConflict = codeKey !== '' && conflictSet.has(codeKey);
    const codeError =
      codeEmpty ||
      codeSpaceError ||
      codeHalfWidthError ||
      codeMaxLenError ||
      codeDuplicate ||
      codeDbConflict;
    const todayStart = getTodayStart();
    const validFromDate = parseYYYYMMDD(row.valid_from);
    const validToDate = parseYYYYMMDD(row.valid_to);
    const validFromError =
      row.valid_from === null ||
      !isValidDate(row.valid_from)
    const validToError =
      row.valid_to !== null &&
      !isValidDate(row.valid_to) ||
      (validFromDate !== null &&
        validToDate !== null &&
        validToDate.isBefore(validFromDate));
    const cogEvoError =
      row.cog_evo === null || (row.cog_evo !== 0 && row.cog_evo !== 1);
    const nounowError =
      row.nounow === null || (row.nounow !== 0 && row.nounow !== 1);
    const limitError =
      row.limit === null ||
      typeof row.limit !== 'number' ||
      Number.isNaN(row.limit) ||
      row.limit <= 0;

    const idValue =
      typeof row.id === 'number' && !Number.isNaN(row.id) ? row.id : null;
    const hasIdValue =
      row.id !== null &&
      row.id !== undefined &&
      typeof row.id === 'number' &&
      !Number.isNaN(row.id);
    const idDuplicate = idValue !== null && seenIds.has(idValue);

    const idError = idInvalid || idDuplicate;

    const hasOtherError =
      codeError ||
      validFromError ||
      validToError ||
      cogEvoError ||
      nounowError ||
      limitError;

    if (idError || hasOtherError) {
      const codeColor = codeDbConflict ? WARNING_CELL_BG : (codeError ? ERROR_CELL_BG : null);
      errors.push({
        id: field(
          row.id_raw ?? row.id,
          idError,
          idError ? ERROR_CELL_BG : null,
        ),
        code: field(row.code, codeError, codeColor),
        valid_from: field(
          row.valid_from,
          validFromError,
          validFromError ? ERROR_CELL_BG : null,
        ),
        valid_to: field(
          row.valid_to,
          validToError,
          validToError ? ERROR_CELL_BG : null,
        ),
        cog_evo: field(
          row.cog_evo_raw ?? row.cog_evo,
          cogEvoError,
          cogEvoError ? ERROR_CELL_BG : null,
        ),
        nounow: field(
          row.nounow_raw ?? row.nounow,
          nounowError,
          nounowError ? ERROR_CELL_BG : null,
        ),
        limit: field(
          row.limit_raw ?? row.limit,
          limitError,
          limitError ? ERROR_CELL_BG : null,
        ),
      });
    } else {
      const sortOrder = row.id;
      const matchedSortOrder = inviteCodes.find(
        (c) => Number(c.sortOrder) === Number(sortOrder),
      );
      const matchedSortOrderCode =
        matchedSortOrder
          ? String(matchedSortOrder.code ?? '').trim()
          : '';
      const matchedCodeOtherSortOrder = inviteCodes.find(
        (c) =>
          String(c.code ?? '').trim() === codeKey &&
          Number(c.sortOrder ?? c.id) !== Number(sortOrder),
      );
      // If sortOrder exists in detail but code differs, mark code as invalid.
      if (
        (matchedSortOrder && matchedSortOrderCode !== codeKey) ||
        !!matchedCodeOtherSortOrder
      ) {
        errors.push({
          id: field(row.id, true, ERROR_CELL_BG),
          code: field(row.code, true, ERROR_CELL_BG),
          valid_from: field(row.valid_from, false, null),
          valid_to: field(row.valid_to, false, null),
          cog_evo: field(row.cog_evo, false, null),
          nounow: field(row.nounow, false, null),
          limit: field(row.limit, false, null),
        });
      } else {
        const matched = inviteCodes.find(
          (c) =>
            String(c.code ?? '').trim() === codeKey &&
            Number(c.sortOrder ?? c.id) === Number(sortOrder),
        );

        if (matched) {
          // Update existing row: include only changed fields.
          const updateItem: ICompanyInviteCodeRequest = {
            id: matched.id,
          };

          const nextValidFrom = toYYYYMMDD(row.valid_from);
          const prevValidFrom = toYYYYMMDD(
            matched.validFrom ? String(matched.validFrom) : null,
          );
          if (nextValidFrom !== prevValidFrom) {
            updateItem.validFrom = nextValidFrom;
          }

          const nextValidTo = toYYYYMMDD(row.valid_to);
          const prevValidTo = toYYYYMMDD(
            matched.validTo ? String(matched.validTo) : null,
          );
          if (nextValidTo !== prevValidTo) {
            updateItem.validTo = nextValidTo;
          }

          const nextQuantity = row.limit ?? null;
          const prevQuantity =
            matched.quantity === undefined ? null : matched.quantity;
          if (nextQuantity !== prevQuantity) {
            updateItem.quantity = nextQuantity;
          }

          const nextCogEvo = row.cog_evo ?? null;
          const prevCogEvo =
            matched.cogEvo === undefined ? null : matched.cogEvo;
          if (nextCogEvo !== prevCogEvo) {
            updateItem.cogEvo = nextCogEvo;
          }

          const nextNouknow = row.nounow ?? null;
          const prevNouknow =
            matched.nouknow === undefined ? null : matched.nouknow;
          if (nextNouknow !== prevNouknow) {
            updateItem.nouknow = nextNouknow;
          }

          // If no fields changed, skip push.
          if (Object.keys(updateItem).length > 1) {
            updateData.push(updateItem);
          }
        } else {
          // Create new row: include full payload.
          if (
            validFromDate !== null &&
            validFromDate.isBefore(todayStart)
          ) {
            errors.push({
              id: field(row.id, false, null),
              code: field(row.code, false, null),
              valid_from: field(row.valid_from, true, ERROR_CELL_BG),
              valid_to: field(row.valid_to, false, null),
              cog_evo: field(row.cog_evo, false, null),
              nounow: field(row.nounow, false, null),
              limit: field(row.limit, false, null),
            });
          } else {
            const createItem: ICompanyInviteCodeRequest = {
              code: row.code ?? null,
              validFrom: row.valid_from ?? null,
              validTo: row.valid_to ?? null,
              sortOrder: sortOrder ?? null,
              quantity: row.limit ?? null,
              cogEvo: row.cog_evo ?? null,
              nouknow: row.nounow ?? null,
            };
            updateData.push(createItem);
          }
        }

        success.push(row);
      }
    }
    // Luôn ghi nhận id/code đã gặp (dù dòng vào success hay error) để phát hiện trùng ở các dòng sau
    if (idValue !== null) seenIds.add(idValue);
    if (codeKey !== '') seenCodes.add(codeKey);
  }

  errors.sort((a, b) => {
    const aNum =
      typeof a.id.value === 'number' ? a.id.value : Number.MAX_SAFE_INTEGER;
    const bNum =
      typeof b.id.value === 'number' ? b.id.value : Number.MAX_SAFE_INTEGER;
    return aNum - bNum;
  });
  return { errors, success, updateData };
}
